import {
  BulkDeleteButton,
  useListController,
  BulkUpdateButton,
  useNotify,
} from "react-admin";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { Box, Button, Chip, Divider } from "@mui/material";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import React, { useEffect, useRef, useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import moment from "moment";
import { colors, radius, shadows } from "../../data/tokens";
import Select from "@mui/material/Select";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Card } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import LinearProgress from "@mui/material/LinearProgress";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormLabel from "@mui/material/FormLabel";
import {
  HiOutlineCog,
  HiOutlineCollection,
  HiOutlineEye,
  HiOutlineEyeOff,
} from "react-icons/hi";
import { assignCustomer } from "./UserUtils";
import {
  createIuguCustomer,
  findIuguCustomer,
  isSingleEmail,
  iuguErrorMessage,
  SEARCH_RETRY_LONG_MS,
} from "./IuguUtils";

// Pendências das operações em massa agrupadas por motivo. Guardar a lista
// inteira (até ~20 mil sócios sem e-mail) e reagrupar a cada sócio
// processado travaria a tela; o grupo guarda só a contagem e exemplos.
type PendingGroup = { reason: string; count: number; examples: string[] };
const PENDING_EXAMPLES = 5;

// Uma operação por aba: duas rodadas em paralelo buscariam o mesmo sócio
// antes de qualquer uma criar a conta IUGU (duplicata), mandariam o e-mail
// duas vezes ou gerariam a mesma matrícula para dois sócios.
let iuguRunActive = false;
let taskRunActive = false;

// E-mail e matrícula em massa (um sócio por vez)
type MemberTask = "mail" | "registration";
type TaskOutcome = { kind: "ok" | "skipped" | "failed"; reason?: string };
const TASK_LABELS: Record<MemberTask, { ok: string }> = {
  mail: { ok: "enviados" },
  registration: { ok: "matrículas geradas" },
};
// Pausa depois de cada envio: o código antigo já esperava 2 s entre
// e-mails (sem querer) e o servidor de e-mail registra erros de fila (451)
// — o ritmo foi mantido de propósito. A matrícula também manda e-mail.
const TASK_PACE_MS: Record<MemberTask, number> = {
  mail: 2000,
  registration: 1000,
};

/** Mensagem curta para agrupar: sem endereços (cada um viraria um grupo). */
const shortReason = (message?: string) =>
  (message || "")
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+/g, "(e-mail)")
    .slice(0, 140);

/** Texto do botão Executar: com a rodada em andamento, o "X de Y" no topo. */
const runningLabel = (progress: any) => {
  if (!progress?.running) return "Executar";
  if (!progress.total) return "Carregando...";
  return `Processando ${progress.done.toLocaleString(
    "pt-BR"
  )}/${progress.total.toLocaleString("pt-BR")}`;
};

const readJsonSafe = async (response: Response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

// Fechar/recarregar a aba interrompe a rodada: o navegador pede confirmação
const warnBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = "";
};

// Título das seções internas do modal (mesmo padrão dos rótulos da ficha)
const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: "uppercase" as const,
  color: colors.textMuted,
};

// Campos de data do sócio com nome amigável — usados no seletor de
// "Alterar campos de data" e nos textos de efeito (nunca mostrar o
// nome técnico do campo para o usuário)
const dateFieldOptions = [
  { id: "createdAt", name: "Criação da conta" },
  { id: "updatedAt", name: "Atualização da conta" },
  { id: "effectiveDate", name: "Efetivo" },
  { id: "admissionDate", name: "Admissão" },
  { id: "licenseDate", name: "Licenciado" },
  { id: "collectiveDate", name: "Coletivo" },
  { id: "remissionDate", name: "Remido" },
  { id: "aspirantDate", name: "Aspirante" },
  { id: "cancellationDate", name: "Cancelado" },
  { id: "collectiveRemissionDate", name: "Coletivo Remido" },
  { id: "birthDate", name: "Nascimento" },
  { id: "contributionDate", name: "Contribuinte" },
  { id: "deceasedDate", name: "Falecimento" },
  { id: "seniorDate", name: "Sênior" },
];

const dateFieldLabel = (id: string) =>
  dateFieldOptions.find((option) => option.id === id)?.name ?? id;

export const PostBulkActionButtons = (props: any) => {
  const notify = useNotify();
  const [userCategories, setUserCategories] = useState<any>([]);
  const [mailTemplates, setMailTemplates] = useState<any>([]);

  const [bulkAction, setBulkAction] = useState("");
  const [changeEffects, setChangeEffects] = useState<any>([]);
  const [confirmBulkAction, setConfirmBulkAction] = useState(false);
  const [bulkDateField, setBulkDateField] = useState<any>(moment());
  const listContext = useListController();
  const [dateField, setDateField] = useState("");
  const [passwordField, setPasswordField] = useState("");
  const [showBulkPassword, setShowBulkPassword] = useState(false);
  const [bulkValueObj, setBulkValueObj] = useState<any>({});
  const [open, setOpen] = React.useState(false);
  const [statusDateRelates, setStatusDateRelates] = useState(false);
  const [categoryDateRelates, setCategoryDateRelates] = useState(false);
  // IUGU: alcance da ação (página atual x todos os filtrados) e progresso
  const [iuguScope, setIuguScope] = useState<"selected" | "filtered">(
    "selected"
  );
  const [iuguProgress, setIuguProgress] = useState<any>(null);
  const iuguCancelRef = useRef(false);
  // E-mail e matrícula em massa: progresso e resumo (antes não havia nenhum)
  const [taskProgress, setTaskProgress] = useState<any>(null);
  const taskCancelRef = useRef(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const anyRunning = !!(iuguProgress?.running || taskProgress?.running);

  // Ao iniciar, leva a tela até o andamento — ele fica no fim da janela e,
  // com a lista de contas longa, era preciso rolar para ver a barra
  useEffect(() => {
    if (anyRunning) {
      progressRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [anyRunning]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const clearBulkData = () => {
    setBulkAction("");
    setChangeEffects([]);
    setConfirmBulkAction(false);
    setBulkDateField(moment());
    setDateField("");
    setBulkValueObj({});
    setStatusDateRelates(false);
    setCategoryDateRelates(false);
    setPasswordField("");
    setShowBulkPassword(false);
    setIuguScope("selected");
    setIuguProgress(null);
    iuguCancelRef.current = false;
    setTaskProgress(null);
    taskCancelRef.current = false;
  };
  // Fechar sempre limpa o estado — reabrir começa do zero
  const closeAndClear = () => {
    handleClose();
    clearBulkData();
  };
  // Fechar não interrompia a operação: o laço seguia rodando escondido e
  // uma nova execução corria em paralelo (duplicatas).
  const requestClose = () => {
    if (anyRunning) {
      notify(
        'Há uma operação em andamento. Use "Interromper" para parar antes de fechar.',
        { type: "warning" }
      );
      return;
    }
    closeAndClear();
  };
  const accountStatus: any = [
    { id: "ACTIVE", name: "Ativo" },
    { id: "CANCELED", name: "Cancelado" },
    { id: "NO_CONTACT", name: "Sem contato" },
    { id: "LICENSED", name: "Licenciado" },
    { id: "DECEASED", name: "Falecido" },
    { id: "UNKNOWN", name: "Desconhecido" },
  ];
  const statusName = (id: any) =>
    accountStatus.find((status: any) => status.id === id)?.name ?? id;
  const categoryName = (id: any) =>
    userCategories.find((category: any) => category.id === id)?.name ?? "";

  // ----------------------------------------------------------------
  // IUGU em massa
  // ----------------------------------------------------------------

  /** Sócios da página atual que estão marcados. */
  const getSelectedMembers = () =>
    (listContext.data || []).filter((record: any) =>
      listContext.selectedIds.includes(record.id)
    );

  /**
   * Todos os sócios que atendem aos filtros da tela, não só a página.
   * Reaproveita o endpoint de exportação, que aplica os mesmos filtros
   * sem limite de paginação.
   */
  const fetchFilteredMembers = async () => {
    const auth = localStorage.getItem("auth");
    const authJson = auth ? JSON.parse(auth) : auth;
    const columns = "id,fullName,email,cpf,cnpj,iuguId";
    const filter = JSON.stringify(listContext.filterValues || {});

    const response = await fetch(
      `${process.env.REACT_APP_API_PATH}/users/export/${columns}/?filter=${encodeURIComponent(
        filter
      )}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authJson.access_token,
        },
      }
    );
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  /**
   * Percorre os sócios verificando primeiro se já existe conta na IUGU
   * (por e-mail e por CPF/CNPJ). Só cria conta nova quando não existe —
   * é isso que evita as duplicatas.
   */
  const runIuguLinking = async () => {
    if (iuguRunActive || taskRunActive) {
      notify("Já há uma operação em massa em andamento nesta aba.", {
        type: "warning",
      });
      return;
    }
    iuguRunActive = true;
    iuguCancelRef.current = false;
    window.addEventListener("beforeunload", warnBeforeUnload);

    // Cada sócio cai em exatamente um destes contadores
    const summary = {
      running: true,
      done: 0,
      total: 0,
      created: 0,
      linked: 0,
      alreadyLinked: 0,
      pendingTotal: 0,
      pending: [] as PendingGroup[],
    };
    const addPending = (record: any, reason: string) => {
      let group = summary.pending.find((item) => item.reason === reason);
      if (!group) {
        group = { reason, count: 0, examples: [] };
        summary.pending.push(group);
      }
      group.count += 1;
      if (group.examples.length < PENDING_EXAMPLES) {
        group.examples.push(record.fullName || `ID ${record.id}`);
      }
      summary.pendingTotal += 1;
    };

    try {
      setIuguProgress({ ...summary });

      let members: any[] = [];
      try {
        members =
          iuguScope === "filtered"
            ? await fetchFilteredMembers()
            : getSelectedMembers();
      } catch (error) {
        notify("Não foi possível carregar a lista de sócios.", {
          type: "error",
        });
        setIuguProgress(null);
        return;
      }

      summary.total = members.length;
      setIuguProgress({ ...summary });

      for (const record of members) {
        if (iuguCancelRef.current) break;

        const document = record.cnpj || record.cpf;

        if (record.iuguId) {
          summary.alreadyLinked += 1;
        } else if (!record.email || !document || !record.fullName) {
          // A IUGU exige e-mail; sem documento não há como checar duplicata
          addPending(record, "Sem e-mail ou CPF/CNPJ na ficha");
        } else {
          try {
            // Esperas longas quando a IUGU barra (limite de requisições),
            // com aviso na tela; "Interromper" corta a espera
            const match = await findIuguCustomer(record, {
              retryDelaysMs: SEARCH_RETRY_LONG_MS,
              onRetryWait: (ms) =>
                setIuguProgress({
                  ...summary,
                  waiting: `Aguardando a IUGU liberar novas consultas (limite de requisições) — nova tentativa em ${Math.round(
                    ms / 1000
                  )} s`,
                }),
              shouldStop: () => iuguCancelRef.current,
            });

            if (match.customer?.id) {
              if (await assignCustomer(match.customer.id, record.id, false)) {
                summary.linked += 1;
              } else {
                addPending(
                  record,
                  "Conta encontrada na IUGU, mas o vínculo não foi gravado (pode já estar vinculada a outro sócio)"
                );
              }
            } else if (match.candidates.length > 0) {
              // Achou parecidos, mas nada com e-mail ou documento idêntico.
              // Criar aqui é justamente o que gerava duplicata: deixa para
              // conferência manual no perfil do sócio.
              addPending(
                record,
                "Cadastro parecido na IUGU — conferir no perfil antes de criar"
              );
            } else if (!isSingleEmail(record.email)) {
              // Mais de um e-mail no campo, espaço, sem @... A IUGU recusaria
              // o cadastro; a conta só pode ser vinculada se já existir
              // (achada pelo documento, acima).
              addPending(
                record,
                "E-mail da ficha inválido (mais de um endereço ou fora do formato) — corrigir a ficha"
              );
            } else {
              const created = await createIuguCustomer({
                email: record.email,
                name: record.fullName,
                cpf_cnpj: document,
              });
              if (await assignCustomer(created.id, record.id, false)) {
                summary.created += 1;
              } else {
                addPending(
                  record,
                  "Conta criada na IUGU, mas o vínculo não foi gravado — rode de novo ou vincule pelo perfil"
                );
              }
            }
          } catch (error) {
            // Interrompido no meio de uma espera: o sócio não conta
            if (iuguCancelRef.current) break;
            // Motivo real (ex.: "a IUGU recusou o cadastro: E-mail
            // inválido"); antes tudo virava "a IUGU não retornou o cadastro"
            const reason = iuguErrorMessage(error);
            addPending(record, reason.charAt(0).toUpperCase() + reason.slice(1));
          }
        }

        summary.done += 1;
        setIuguProgress({ ...summary });
      }

      setIuguProgress({ ...summary, running: false });
      const counts = `${summary.created} criados, ${summary.linked} vinculados a contas já existentes, ${summary.alreadyLinked} já estavam vinculados, ${summary.pendingTotal} com pendência`;
      // "Tudo certo" (verde) só quando não sobrou pendência
      notify(
        iuguCancelRef.current
          ? `Interrompido em ${summary.done} de ${summary.total}: ${counts}.`
          : `Concluído: ${counts}.`,
        {
          type:
            iuguCancelRef.current || summary.pendingTotal > 0
              ? "warning"
              : "success",
        }
      );
      listContext.refetch?.();
    } finally {
      iuguRunActive = false;
      window.removeEventListener("beforeunload", warnBeforeUnload);
    }
  };

  // ----------------------------------------------------------------
  // E-mail e matrícula em massa
  // ----------------------------------------------------------------

  const apiHeaders = () => {
    const auth = localStorage.getItem("auth");
    const authJson = auth ? JSON.parse(auth) : auth;
    return {
      "Content-Type": "application/json",
      Authorization: "Bearer " + authJson?.access_token,
    };
  };

  /**
   * Percorre os sócios marcados um por vez, com progresso e resumo. Antes:
   * a janela fechava depois do 1º sócio (o resto seguia escondido), não
   * havia progresso, clique duplo repetia tudo e, na matrícula, o 1º erro
   * abandonava os seguintes sem aviso. Agora o erro de um sócio não
   * interrompe os demais e cada um cai em: concluído, pulado ou falha.
   */
  const runMemberTask = async (
    task: MemberTask,
    processOne: (record: any) => Promise<TaskOutcome>
  ) => {
    if (iuguRunActive || taskRunActive) {
      notify("Já há uma operação em massa em andamento nesta aba.", {
        type: "warning",
      });
      return;
    }
    taskRunActive = true;
    taskCancelRef.current = false;
    window.addEventListener("beforeunload", warnBeforeUnload);

    const members = getSelectedMembers();
    const summary = {
      task,
      running: true,
      done: 0,
      total: members.length,
      ok: 0,
      skipped: 0,
      failed: 0,
      pendingTotal: 0,
      pending: [] as PendingGroup[],
    };
    const addGroup = (record: any, reason: string) => {
      let group = summary.pending.find((item) => item.reason === reason);
      if (!group) {
        group = { reason, count: 0, examples: [] };
        summary.pending.push(group);
      }
      group.count += 1;
      if (group.examples.length < PENDING_EXAMPLES) {
        group.examples.push(record.fullName || `ID ${record.id}`);
      }
      summary.pendingTotal += 1;
    };

    try {
      setTaskProgress({ ...summary });
      for (const record of members) {
        if (taskCancelRef.current) break;

        let outcome: TaskOutcome;
        try {
          outcome = await processOne(record);
        } catch (error) {
          outcome = {
            kind: "failed",
            reason: "Falha — sem resposta do servidor",
          };
        }
        summary[outcome.kind] += 1;
        if (outcome.reason) addGroup(record, outcome.reason);
        summary.done += 1;
        setTaskProgress({ ...summary });

        // Ritmo só depois de uma chamada de verdade (pulado não chama nada)
        if (outcome.kind !== "skipped" && summary.done < summary.total) {
          await new Promise((resolve) =>
            setTimeout(resolve, TASK_PACE_MS[task])
          );
        }
      }

      setTaskProgress({ ...summary, running: false });
      const counts = `${summary.ok} ${TASK_LABELS[task].ok}, ${summary.skipped} pulados, ${summary.failed} com falha`;
      const interrupted = taskCancelRef.current;
      notify(
        interrupted
          ? `Interrompido em ${summary.done} de ${summary.total}: ${counts}.`
          : `Concluído: ${counts}.`,
        {
          type:
            interrupted || summary.skipped || summary.failed
              ? "warning"
              : "success",
        }
      );
      if (task === "registration") listContext.refetch?.();
    } finally {
      taskRunActive = false;
      window.removeEventListener("beforeunload", warnBeforeUnload);
    }
  };

  // Envia o template escolhido para cada sócio marcado
  const sendMailTo = async (record: any): Promise<TaskOutcome> => {
    if (!record.email) {
      return { kind: "skipped", reason: "Pulado — sem e-mail na ficha" };
    }
    const response = await fetch(
      `${process.env.REACT_APP_API_PATH}/users/mail/send/${record.id}/${bulkValueObj.templateId}`,
      { method: "POST", headers: apiHeaders() }
    );
    const data = await readJsonSafe(response);
    if (response.ok && data?.type === "success") return { kind: "ok" };
    if (response.ok && !data) {
      return { kind: "failed", reason: "Falha — modelo de e-mail não encontrado" };
    }
    return {
      kind: "failed",
      reason: `Falha no envio — ${
        shortReason(data?.message) || `HTTP ${response.status}`
      }`,
    };
  };

  /** A ficha do sócio já tem matrícula gravada? */
  const hasRegistrationNumber = async (memberId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}/users/crud/${memberId}`,
        { headers: apiHeaders() }
      );
      const data = await readJsonSafe(response);
      return Number(data?.registrationNumber) > 0;
    } catch (error) {
      return false;
    }
  };

  // Gera número de matrícula para cada sócio marcado
  const assignRegistrationTo = async (record: any): Promise<TaskOutcome> => {
    // Regra que já existia, mantida: sem e-mail não gera (o servidor manda
    // as boas-vindas na geração)
    if (!record.email) {
      return { kind: "skipped", reason: "Pulado — sem e-mail na ficha" };
    }
    if (Number(record.registrationNumber) > 0) {
      return { kind: "skipped", reason: "Pulado — já possui matrícula" };
    }
    const response = await fetch(
      `${process.env.REACT_APP_API_PATH}/users/assign-registration-number/${record.id}`,
      { method: "POST", headers: apiHeaders() }
    );
    const data = await readJsonSafe(response);
    if (response.ok && data?.id) return { kind: "ok" };

    const message = shortReason(data?.message);
    if (/já possui matrícula/i.test(message)) {
      return { kind: "skipped", reason: "Pulado — já possui matrícula" };
    }
    // O servidor grava a matrícula e SÓ DEPOIS manda as boas-vindas: se o
    // e-mail falha, ele responde erro com a matrícula já gravada. Confere a
    // ficha antes de dizer que falhou.
    if (await hasRegistrationNumber(record.id)) {
      return {
        kind: "ok",
        reason: `Matrícula gerada, mas o e-mail de boas-vindas falhou — ${
          message || `HTTP ${response.status}`
        }`,
      };
    }
    return {
      kind: "failed",
      reason: `Falha — ${message || `HTTP ${response.status}`}`,
    };
  };

  const runMailSending = () => runMemberTask("mail", sendMailTo);
  const runAssignRegistration = () =>
    runMemberTask("registration", assignRegistrationTo);

  /** Pendências agrupadas por motivo, com contagem e exemplos de nomes. */
  const renderGroups = (progress: any, header: string) =>
    progress.pendingTotal > 0 && (
      <Alert
        severity="warning"
        sx={{
          marginTop: 1,
          borderRadius: `${radius.sm}px`,
          fontSize: 13,
        }}
      >
        {progress.pendingTotal.toLocaleString("pt-BR")} {header}
        {progress.pending.map((group: PendingGroup) => (
          <Box key={group.reason} sx={{ marginTop: 1 }}>
            <b>{group.reason}</b> — {group.count.toLocaleString("pt-BR")}
            <Typography
              variant="caption"
              sx={{ display: "block", color: colors.textSecondary }}
            >
              ex.: {group.examples.join(", ")}
              {group.count > group.examples.length && "…"}
            </Typography>
          </Box>
        ))}
      </Alert>
    );

  const ExecuteButton = (props: any) => {
    const { label, data } = props;

    // O react-admin fixa a cor do texto no styled root do botão — o
    // visual sólido (padrão das ações primárias do painel) entra por sx
    return (
      <BulkUpdateButton
        label={label}
        data={data}
        onClick={closeAndClear}
        icon={<HiOutlineCog size={18} />}
        sx={{
          backgroundColor: colors.primary,
          color: `${colors.white} !important`,
          paddingLeft: 2,
          paddingRight: 2,
          "&:hover": { backgroundColor: colors.primaryDark },
        }}
      />
    );
  };
  const fetchUserCategories = async () => {
    const auth = localStorage.getItem("auth");
    const authJson = auth ? JSON.parse(auth) : auth;
    const response = await fetch(
      `${process.env.REACT_APP_API_PATH}/user-categories/crud`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authJson.access_token,
        },
      }
    );
    const data = await response.json();
    setUserCategories(data);

    return data;
  };
  const fetchMailTemplates = async () => {
    const auth = localStorage.getItem("auth");
    const authJson = auth ? JSON.parse(auth) : auth;
    const response = await fetch(
      `${process.env.REACT_APP_API_PATH}/mail-templates/crud`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authJson.access_token,
        },
      }
    );
    const responseData = await response.json();

    if (!response.ok) {
      notify("Erro ao carregar templates de e-mail", { type: "error" });
    }
    setMailTemplates(responseData);

    return responseData;
  };

  const selectedCount = listContext.selectedIds?.length || 0;
  const selectedMembers = getSelectedMembers();

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Button
        size="small"
        variant="contained"
        startIcon={<HiOutlineCollection size={16} />}
        onClick={() => {
          handleOpen();
          fetchUserCategories();
          fetchMailTemplates();
        }}
      >
        Iniciar operações em massa
      </Button>

      <Modal
        aria-labelledby="bulk-modal-title"
        open={open}
        onClose={(_event, reason) => {
          // Clique fora não fecha: um clique acidental perdia a ação e os
          // valores já preenchidos. Fecha pelo "Cancelar" ou pelo Esc.
          if (reason === "backdropClick") return;
          requestClose();
        }}
        closeAfterTransition
      >
        <Fade in={open}>
          {/* pointerEvents: o Box centralizador deixa os cliques fora do
              cartão passarem para o backdrop (fechar ao clicar fora) */}
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 2,
              pointerEvents: "none",
            }}
          >
            <Card
              sx={{
                pointerEvents: "auto",
                width: "min(880px, 96vw)",
                maxHeight: "88vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: `${radius.lg}px`,
                boxShadow: shadows.popover,
              }}
            >
              {/* Cabeçalho fixo: título + ações sempre visíveis, sem rolagem */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                  padding: "16px 24px",
                  borderBottom: `1px solid ${colors.border}`,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                  <Typography
                    id="bulk-modal-title"
                    sx={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: colors.textStrong,
                    }}
                  >
                    Operações em massa
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                    {selectedCount === 1
                      ? "1 sócio marcado nesta página"
                      : `${selectedCount} sócios marcados nesta página`}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={requestClose}
                  disabled={anyRunning}
                  sx={{ color: colors.textMuted }}
                >
                  Cancelar
                </Button>
                {confirmBulkAction &&
                  open &&
                  bulkAction &&
                  bulkAction !== "bulkActionDelete" &&
                  bulkAction !== "bulkActionIUGU" &&
                  bulkAction !== "bulkActionAssignRegistrationNumber" &&
                  bulkAction !== "bulkActionMail" && (
                    <ExecuteButton label="Executar" data={bulkValueObj} />
                  )}
                {confirmBulkAction && bulkAction === "bulkActionDelete" && (
                  <BulkDeleteButton
                    onClick={closeAndClear}
                    sx={{
                      backgroundColor: colors.action,
                      color: `${colors.white} !important`,
                      paddingLeft: 2,
                      paddingRight: 2,
                      "&:hover": {
                        backgroundColor: colors.action,
                        filter: "brightness(0.92)",
                      },
                    }}
                  />
                )}
                {confirmBulkAction && bulkAction === "bulkActionIUGU" && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<HiOutlineCog size={16} />}
                    disabled={anyRunning}
                    onClick={runIuguLinking}
                  >
                    {runningLabel(iuguProgress)}
                  </Button>
                )}
                {/* Bloqueado durante a rodada: clique duplo mandava o
                    e-mail (ou gerava a matrícula) duas vezes */}
                {confirmBulkAction && bulkAction === "bulkActionMail" && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<HiOutlineCog size={16} />}
                    disabled={anyRunning}
                    onClick={runMailSending}
                  >
                    {runningLabel(taskProgress)}
                  </Button>
                )}
                {confirmBulkAction &&
                  bulkAction === "bulkActionAssignRegistrationNumber" && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<HiOutlineCog size={16} />}
                      disabled={anyRunning}
                      onClick={runAssignRegistration}
                    >
                      {runningLabel(taskProgress)}
                    </Button>
                  )}
              </Box>

              {/* Corpo: só ele rola em telas baixas; a lista de contas tem
                  rolagem própria e não empurra o restante */}
              <Box
                sx={{
                  padding: "20px 24px 24px",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                {/* Ação e campos complementares, lado a lado */}
                <Box>
                  <Typography sx={sectionLabelSx}>
                    Selecione uma ação
                  </Typography>
                  <Grid container spacing={2} sx={{ marginTop: -1 }}>
                    <Grid item xs={12} sm={6}>
                      {/* Trocar a ação no meio da rodada limpava o andamento
                          e deixava a rodada rodando escondida */}
                      <FormControl
                        sx={{ width: "100%" }}
                        disabled={anyRunning}
                      >
                        <InputLabel id="bulk-modal-action-helper-label">
                          Ação
                        </InputLabel>
                        <Select
                          labelId="bulk-modal-action-helper-label"
                          id="bulk-modal-action-helper"
                          label="Ação"
                          value={bulkAction}
                          onChange={(e) => {
                            clearBulkData();
                            setBulkAction(e.target.value as string);

                            if (e.target.value === "bulkActionDelete") {
                              setChangeEffects([
                                "As contas serão removidas permanentemente.",
                              ]);
                            }
                            if (e.target.value === "bulkActionIUGU") {
                              setChangeEffects([
                                "Serão processados apenas os sócios marcados nesta página.",
                                "Antes de criar, o sistema procura conta existente na IUGU por e-mail e por CPF/CNPJ. Quem já tiver conta é apenas vinculado. Quem já tem vínculo é ignorado.",
                              ]);
                            }
                            if (e.target.value === "bulkActionMail") {
                              setChangeEffects([
                                "As contas selecionadas receberão um e-mail conforme o template selecionado.",
                              ]);
                            }
                            if (
                              e.target.value ===
                              "bulkActionAssignRegistrationNumber"
                            ) {
                              setChangeEffects([
                                "Será gerado um número de matrícula para todas as contas selecionadas.",
                              ]);
                            }
                          }}
                          sx={{ width: "100%" }}
                        >
                          <MenuItem
                            value={"bulkActionAssignRegistrationNumber"}
                          >
                            Designar Matrícula(s)
                          </MenuItem>
                          <MenuItem value={"bulkActionDelete"}>
                            Remover contas
                          </MenuItem>
                          <MenuItem value={"bulkActionMail"}>
                            Enviar E-mail
                          </MenuItem>
                          <MenuItem value={"bulkActionHasCard"}>
                            Alterar carteirinha
                          </MenuItem>
                          <MenuItem value={"bulkActionDate"}>
                            Alterar campos de data
                          </MenuItem>
                          <MenuItem value={"bulkActionIUGU"}>
                            Criar e Vincular com IUGU
                          </MenuItem>
                          <Divider />
                          <MenuItem value={"bulkActionCategory"}>
                            Alteração de Categoria
                          </MenuItem>
                          <MenuItem value={"bulkActionStatus"}>
                            Alteração de Situação
                          </MenuItem>
                          <MenuItem value={"bulkActionPassword"}>
                            Alteração de Senha
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {bulkAction === "bulkActionHasCard" && (
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: "100%" }}>
                          <InputLabel id="bulk-modal-hascard-label">
                            Possui Carteirinha?
                          </InputLabel>
                          <Select
                            labelId="bulk-modal-hascard-label"
                            label="Possui Carteirinha?"
                            value={
                              bulkValueObj.hasPhysicalCard === undefined
                                ? ""
                                : String(bulkValueObj.hasPhysicalCard)
                            }
                            onChange={(e) => {
                              setBulkValueObj({
                                hasPhysicalCard:
                                  e.target.value === "true" ? true : false,
                              });
                              setChangeEffects([
                                `A carteirinha das contas selecionadas será alterada para “${
                                  e.target.value === "true" ? "Sim" : "Não"
                                }”.`,
                              ]);
                            }}
                            sx={{ width: "100%" }}
                          >
                            <MenuItem value={"true"}>Sim</MenuItem>
                            <MenuItem value={"false"}>Não</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {bulkAction === "bulkActionMail" && (
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          sx={{ width: "100%" }}
                          disabled={anyRunning}
                        >
                          <InputLabel id="bulk-modal-mail-label">
                            Template de E-mail
                          </InputLabel>
                          <Select
                            labelId="bulk-modal-mail-label"
                            label="Template de E-mail"
                            value={bulkValueObj.templateId ?? ""}
                            onChange={(e) => {
                              setBulkValueObj({
                                templateId: e.target.value,
                              });
                            }}
                            sx={{ width: "100%" }}
                          >
                            {mailTemplates.length > 0 &&
                              mailTemplates.map((mail: any) => (
                                <MenuItem key={mail.id} value={mail.id}>
                                  {mail.subject}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {bulkAction === "bulkActionCategory" && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControl sx={{ width: "100%" }}>
                            <InputLabel id="bulk-modal-category-label">
                              Categoria de Membro
                            </InputLabel>
                            <Select
                              labelId="bulk-modal-category-label"
                              label="Categoria de Membro"
                              value={bulkValueObj.userCategoryId ?? ""}
                              onChange={(e) => {
                                setBulkValueObj({
                                  userCategoryId: e.target.value,
                                });
                                setChangeEffects([
                                  `Todas as contas selecionadas serão alteradas para a categoria “${categoryName(
                                    e.target.value
                                  )}”.`,
                                ]);
                                setCategoryDateRelates(false);
                              }}
                              sx={{ width: "100%" }}
                            >
                              {userCategories.length > 0 &&
                                userCategories.map((category: any) => (
                                  <MenuItem
                                    key={category.id}
                                    value={category.id}
                                  >
                                    {category.name}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={categoryDateRelates}
                                onChange={(e) => {
                                  if (bulkValueObj.userCategoryId) {
                                    setCategoryDateRelates(e.target.checked);

                                    const selectedCategoryName = categoryName(
                                      bulkValueObj.userCategoryId
                                    );

                                    const propertyName =
                                      selectedCategoryName === "COLETIVO"
                                        ? "collectiveDate"
                                        : selectedCategoryName === "REMIDO"
                                        ? "remissionDate"
                                        : selectedCategoryName === "ASPIRANTE"
                                        ? "aspirantDate"
                                        : selectedCategoryName ===
                                          "COLETIVO REMIDO"
                                        ? "collectiveRemissionDate"
                                        : selectedCategoryName ===
                                          "CONTRIBUINTE"
                                        ? "contributionDate"
                                        : selectedCategoryName === "EFETIVO"
                                        ? "effectiveDate"
                                        : selectedCategoryName === "SÊNIOR"
                                        ? "seniorDate"
                                        : "";

                                    if (e.target.checked) {
                                      setBulkValueObj({
                                        ...bulkValueObj,
                                        ...(propertyName && {
                                          [propertyName]: moment()
                                            .utc()
                                            .format(),
                                        }),
                                      });
                                      setChangeEffects([
                                        ...changeEffects,
                                        `A data de “${dateFieldLabel(
                                          propertyName
                                        )}” também será atualizada para agora.`,
                                      ]);
                                    } else {
                                      if (bulkValueObj.userCategoryId) {
                                        setBulkValueObj({
                                          userCategoryId:
                                            bulkValueObj.userCategoryId,
                                        });

                                        setChangeEffects([
                                          `Todas as contas selecionadas serão alteradas para a categoria “${selectedCategoryName}”.`,
                                        ]);
                                      }
                                    }
                                  }
                                }}
                              />
                            }
                            label={`Atualizar a data de “${categoryName(
                              bulkValueObj.userCategoryId
                            )}” para agora?`}
                          />
                        </Grid>
                      </>
                    )}
                    {bulkAction === "bulkActionPassword" && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Nova senha"
                          name={"password"}
                          value={passwordField}
                          fullWidth
                          autoComplete="new-password"
                          onChange={(e) => {
                            setPasswordField(e.target.value);
                            setBulkValueObj({
                              password: e.target.value,
                            });
                            setChangeEffects([
                              "Todas as contas selecionadas terão a senha substituída pela nova senha informada.",
                            ]);
                          }}
                          type={showBulkPassword ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={
                                    showBulkPassword
                                      ? "Ocultar senha"
                                      : "Mostrar senha"
                                  }
                                  onClick={() =>
                                    setShowBulkPassword(
                                      (current) => !current
                                    )
                                  }
                                  edge="end"
                                >
                                  {showBulkPassword ? (
                                    <HiOutlineEyeOff size={18} />
                                  ) : (
                                    <HiOutlineEye size={18} />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}
                    {bulkAction === "bulkActionStatus" && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControl sx={{ width: "100%" }}>
                            <InputLabel id="bulk-modal-status-label">
                              Situação Membro
                            </InputLabel>
                            <Select
                              labelId="bulk-modal-status-label"
                              label="Situação Membro"
                              value={bulkValueObj.accountStatus ?? ""}
                              onChange={(e) => {
                                setBulkValueObj({
                                  accountStatus: e.target.value,
                                });
                                setChangeEffects([
                                  `Todas as contas selecionadas terão a situação alterada para “${statusName(
                                    e.target.value
                                  )}”.`,
                                ]);
                                setStatusDateRelates(false);
                              }}
                              sx={{ width: "100%" }}
                            >
                              {accountStatus.map((status: any) => (
                                <MenuItem key={status.id} value={status.id}>
                                  {status.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {(bulkValueObj.accountStatus === "CANCELED" ||
                          bulkValueObj.accountStatus === "LICENSED" ||
                          bulkValueObj.accountStatus === "DECEASED") && (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={statusDateRelates}
                                  onChange={(e) => {
                                    setStatusDateRelates(e.target.checked);
                                    const propertyName =
                                      bulkValueObj.accountStatus === "CANCELED"
                                        ? "cancellationDate"
                                        : bulkValueObj.accountStatus ===
                                          "LICENSED"
                                        ? "licenseDate"
                                        : bulkValueObj.accountStatus ===
                                          "DECEASED"
                                        ? "deceasedDate"
                                        : "";
                                    if (e.target.checked) {
                                      setBulkValueObj({
                                        ...bulkValueObj,
                                        ...(propertyName && {
                                          [propertyName]: moment()
                                            .utc()
                                            .format(),
                                        }),
                                      });
                                      setChangeEffects([
                                        ...changeEffects,
                                        `A data de “${dateFieldLabel(
                                          propertyName
                                        )}” também será atualizada para agora.`,
                                      ]);
                                    } else {
                                      if (bulkValueObj.accountStatus) {
                                        setBulkValueObj({
                                          accountStatus:
                                            bulkValueObj.accountStatus,
                                        });

                                        setChangeEffects([
                                          `Todas as contas selecionadas terão a situação alterada para “${statusName(
                                            bulkValueObj.accountStatus
                                          )}”.`,
                                        ]);
                                      }
                                    }
                                  }}
                                />
                              }
                              label={`Atualizar a data de “${statusName(
                                bulkValueObj.accountStatus
                              )}” para agora?`}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    {bulkAction === "bulkActionIUGU" && (
                      <Grid item xs={12}>
                        <FormControl
                          sx={{ width: "100%" }}
                          disabled={!!iuguProgress?.running}
                        >
                          <FormLabel>Aplicar a</FormLabel>
                          <RadioGroup
                            value={iuguScope}
                            onChange={(e) => {
                              const scope = e.target.value as
                                | "selected"
                                | "filtered";
                              setIuguScope(scope);
                              setChangeEffects([
                                scope === "filtered"
                                  ? "Serão processados TODOS os sócios que atendem aos filtros da tela, não apenas os da página atual."
                                  : "Serão processados apenas os sócios marcados nesta página.",
                                "Antes de criar, o sistema procura conta existente na IUGU por e-mail e por CPF/CNPJ. Quem já tiver conta é apenas vinculado. Quem já tem vínculo é ignorado.",
                              ]);
                            }}
                          >
                            <FormControlLabel
                              value="selected"
                              control={<Radio />}
                              label={`Sócios marcados nesta página (${listContext.selectedIds?.length || 0})`}
                            />
                            <FormControlLabel
                              value="filtered"
                              control={<Radio />}
                              label={`Todos os sócios filtrados (${listContext.total ?? "?"})`}
                            />
                          </RadioGroup>
                          <FormHelperText>
                            Em listas grandes a operação leva horas: as
                            consultas à IUGU são espaçadas (~75 por minuto)
                            para respeitar o limite da IUGU. Mantenha esta aba
                            aberta até o fim; se for interrompida, rodar de
                            novo continua de onde parou — quem já foi
                            vinculado é pulado.
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                    )}
                    {bulkAction === "bulkActionDate" && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControl sx={{ width: "100%" }}>
                            <InputLabel id="bulk-modal-date-field-label">
                              Campo de Data
                            </InputLabel>
                            <Select
                              labelId="bulk-modal-date-field-label"
                              label="Campo de Data"
                              value={dateField}
                              onChange={(e) => {
                                const now = moment();
                                if (e.target.value === "") {
                                  setBulkValueObj({});
                                  setChangeEffects([]);
                                } else {
                                  const displayDate =
                                    now.format("DD/MM/YYYY HH:mm");
                                  setBulkDateField(now);
                                  setBulkValueObj({
                                    [e.target.value]: now.utc().format(),
                                  });
                                  setChangeEffects([
                                    `O campo “${dateFieldLabel(
                                      e.target.value as string
                                    )}” será alterado para ${displayDate}.`,
                                  ]);
                                }

                                setDateField(e.target.value as string);
                              }}
                              fullWidth
                            >
                              <MenuItem value={""}>Nenhum</MenuItem>
                              {dateFieldOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  {option.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {dateField && (
                          <Grid item xs={12} sm={6}>
                            <LocalizationProvider
                              dateAdapter={AdapterMoment}
                              adapterLocale="pt-br"
                            >
                              <DateTimePicker
                                label="Especificar Data"
                                value={bulkDateField}
                                sx={{ width: "100%" }}
                                disabled={bulkValueObj.resetDate}
                                onChange={(v: any) => {
                                  // formata para exibição ANTES do .utc(),
                                  // que muda o fuso do próprio objeto
                                  const displayDate =
                                    v.format("DD/MM/YYYY HH:mm");
                                  setBulkDateField(v);
                                  setBulkValueObj({
                                    [dateField]: v.utc().format(),
                                  });
                                  setChangeEffects([
                                    `O campo “${dateFieldLabel(
                                      dateField
                                    )}” será alterado para ${displayDate}.`,
                                  ]);
                                }}
                              />
                            </LocalizationProvider>
                            {/** Checkbox (Reset) */}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!bulkValueObj.resetDate}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setBulkValueObj({
                                        [dateField]: null,
                                        resetDate: dateField,
                                      });
                                      setChangeEffects([
                                        `O campo “${dateFieldLabel(
                                          dateField
                                        )}” será limpo (ficará sem data).`,
                                      ]);
                                    } else {
                                      const restored = bulkDateField.clone();
                                      const displayDate = restored
                                        .local()
                                        .format("DD/MM/YYYY HH:mm");
                                      setBulkValueObj({
                                        [dateField]: restored.utc().format(),
                                      });
                                      setChangeEffects([
                                        `O campo “${dateFieldLabel(
                                          dateField
                                        )}” será alterado para ${displayDate}.`,
                                      ]);
                                    }
                                  }}
                                />
                              }
                              label={`Limpar o campo “${dateFieldLabel(
                                dateField
                              )}”`}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </Box>

                {/* Contas afetadas: rolagem própria — o modal não cresce */}
                <Box>
                  <Typography sx={sectionLabelSx}>
                    Contas afetadas ({selectedCount})
                  </Typography>
                  <Box
                    sx={{
                      marginTop: 1,
                      border: `1px solid ${colors.border}`,
                      borderRadius: `${radius.sm}px`,
                      backgroundColor: colors.bgSubtle,
                      padding: 1.25,
                      maxHeight: 180,
                      overflowY: "auto",
                      display: "flex",
                      flexWrap: "wrap",
                      alignContent: "flex-start",
                      gap: 0.75,
                    }}
                  >
                    {selectedMembers.length > 0 ? (
                      selectedMembers.map((record: any) => (
                        <Chip
                          key={record.id}
                          label={record.fullName || record.email || record.id}
                          variant="outlined"
                          sx={{
                            backgroundColor: colors.white,
                            borderColor: colors.border,
                            color: colors.textSecondary,
                            maxWidth: "100%",
                          }}
                        />
                      ))
                    ) : (
                      <Typography
                        sx={{ fontSize: 13, color: colors.textMuted }}
                      >
                        Os sócios marcados estão em outra página da lista.
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Efeitos: consequências da ação, em linguagem simples */}
                {changeEffects.length > 0 && (
                  <Box>
                    <Typography sx={sectionLabelSx}>
                      Efeitos desta ação
                    </Typography>
                    <Box
                      sx={{
                        marginTop: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {changeEffects.map((effect: any, index: number) => (
                        <Alert
                          key={index}
                          severity="warning"
                          sx={{
                            borderRadius: `${radius.sm}px`,
                            fontSize: 13,
                            alignItems: "center",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          {effect}
                        </Alert>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Confirmação: libera o botão Executar do cabeçalho */}
                {bulkAction && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      columnGap: 1.5,
                      border: `1px solid ${colors.border}`,
                      borderRadius: `${radius.sm}px`,
                      backgroundColor: colors.bgSubtle,
                      padding: "2px 14px",
                    }}
                  >
                    <FormControlLabel
                      required
                      control={
                        <Checkbox
                          checked={confirmBulkAction}
                          onChange={(e) =>
                            setConfirmBulkAction(e.target.checked)
                          }
                        />
                      }
                      label="Confirma esta ação?"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: 14,
                          fontWeight: 600,
                          color: colors.textStrong,
                        },
                      }}
                    />
                    {!confirmBulkAction && (
                      <Typography
                        sx={{ fontSize: 12.5, color: colors.textMuted }}
                      >
                        Marque para liberar o botão Executar, no topo.
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Andamento da vinculação IUGU */}
                {confirmBulkAction &&
                  bulkAction === "bulkActionIUGU" &&
                  iuguProgress && (
                    <Box ref={progressRef}>
                      <Typography sx={sectionLabelSx}>Andamento</Typography>
                      <Box sx={{ marginTop: 1 }}>
                        <LinearProgress
                          variant={
                            iuguProgress.total ? "determinate" : "indeterminate"
                          }
                          value={
                            iuguProgress.total
                              ? (iuguProgress.done / iuguProgress.total) * 100
                              : 0
                          }
                          sx={{ borderRadius: 99, height: 8 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            marginTop: 0.5,
                            color: colors.textSecondary,
                          }}
                        >
                          {/* Em "todos os filtrados", a lista leva uns
                              segundos para chegar — antes aparecia "0 de 0" */}
                          {iuguProgress.running && !iuguProgress.total ? (
                            "Carregando a lista de sócios…"
                          ) : (
                            <>
                              {iuguProgress.done} de {iuguProgress.total} —{" "}
                              {iuguProgress.created} criados,{" "}
                              {iuguProgress.linked} vinculados a contas já
                              existentes, {iuguProgress.alreadyLinked} já
                              estavam vinculados, {iuguProgress.pendingTotal}{" "}
                              com pendência
                            </>
                          )}
                        </Typography>

                        {/* Sem isso a barra parava por até 1 min e parecia
                            travada */}
                        {iuguProgress.running && iuguProgress.waiting && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: colors.warning }}
                          >
                            {iuguProgress.waiting}
                          </Typography>
                        )}

                        {iuguProgress.running && (
                          <Button
                            size="small"
                            color="warning"
                            title="Para depois do sócio em andamento"
                            onClick={() => {
                              iuguCancelRef.current = true;
                            }}
                          >
                            Interromper
                          </Button>
                        )}

                        {/* Agrupado por motivo: antes só as 10 primeiras
                            pendências apareciam, sem contagem por motivo */}
                        {renderGroups(
                          iuguProgress,
                          "registro(s) com pendência:"
                        )}
                      </Box>
                    </Box>
                  )}

                {/* Andamento do e-mail e da matrícula em massa */}
                {confirmBulkAction &&
                  taskProgress &&
                  bulkAction ===
                    (taskProgress.task === "mail"
                      ? "bulkActionMail"
                      : "bulkActionAssignRegistrationNumber") && (
                    <Box ref={progressRef}>
                      <Typography sx={sectionLabelSx}>Andamento</Typography>
                      <Box sx={{ marginTop: 1 }}>
                        <LinearProgress
                          variant={
                            taskProgress.total ? "determinate" : "indeterminate"
                          }
                          value={
                            taskProgress.total
                              ? (taskProgress.done / taskProgress.total) * 100
                              : 0
                          }
                          sx={{ borderRadius: 99, height: 8 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            marginTop: 0.5,
                            color: colors.textSecondary,
                          }}
                        >
                          {taskProgress.done} de {taskProgress.total} —{" "}
                          {taskProgress.ok}{" "}
                          {TASK_LABELS[taskProgress.task as MemberTask].ok},{" "}
                          {taskProgress.skipped} pulados, {taskProgress.failed}{" "}
                          com falha
                        </Typography>

                        {taskProgress.running && (
                          <Button
                            size="small"
                            color="warning"
                            title="Para depois do sócio em andamento"
                            onClick={() => {
                              taskCancelRef.current = true;
                            }}
                          >
                            Interromper
                          </Button>
                        )}

                        {renderGroups(taskProgress, "registro(s) para conferir:")}
                      </Box>
                    </Box>
                  )}
              </Box>
            </Card>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};
